import React, { useState, useEffect, useMemo } from 'react'
import Box from '@mui/material/Box'
import {
  DataGridPremium,
  GridColDef,
  GridColumnMenuProps,
  GridSortModel,
  useGridApiRef,
  ColumnHeaderFilterIconButtonProps,
  type GridApiPremium,
} from '@mui/x-data-grid-premium'
import { createTheme, styled, ThemeProvider, type SxProps, type Theme } from '@mui/material/styles'
import { heIL } from '@mui/material/locale'
import { useLocation } from 'react-router-dom'

import { userRows as defaultRows } from '../../mockdata/usersData'
import TableHeader from './TableHeader'
import CustomColumnMenu from '@/components/features/config/columns/CustomColumnMenu'
import CustomColumnFilterMenu from '@/components/features/config/columns/CustomColumnFilterMenu'
import { useTableStore } from '@/store/table/table'
import { useAppTheme } from '@/theme/ThemeContext'
import { getColumnStorageKey, getColumnSettings, saveColumnSettings } from '@/utils/columnStorage'
import { valueNameOf } from '@/utils/codeName'
import {
  cellPassesFilters,
  resolveColumnFilter,
  type FilterKind,
} from '@/components/features/config/columns/Filter/filterKinds'
import { getTableFilterValue } from '@/utils/tableFilterValue'
import { isEmptyTableCellValue } from '@/utils/tableCellValue'
import { isolateNumber } from '@/utils/numberDirection'
import { toExportColumns } from '@/utils/exportUtils'
import type { CellFormat } from '@/utils/cellFormat'
import type { ApiFilter } from '@/store/table/table'
import { gridLoadingSlotProps, gridLoadingSx } from './gridLoading'
import { GRID_LOCALE_TEXT } from '@/theme'

type RowSelectionModelV7 = {
  type: 'include' | 'exclude'
  ids: Set<string | number>
}

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
  borderRadius: '0 !important',
  '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 63, 97, 0.4) !important' : '#E3F5FF !important',
  },
  '& .MuiDataGrid-main': {
    width: '100%',
    backgroundColor: 'transparent'
  }
}))

const SHOWN_ROW_CLASS = 'shown-row'
const ROW = '& .MuiDataGrid-row'

const shownRowClassName = (shownRowId: string | number | null | undefined) =>
  shownRowId === undefined
    ? undefined
    : (params: { id: string | number }) =>
        params.id === shownRowId ? SHOWN_ROW_CLASS : ''

const shownRowSx = (tint: string) => ({
  [`${ROW}.Mui-selected:not(.${SHOWN_ROW_CLASS}), ${ROW}.Mui-selected:not(.${SHOWN_ROW_CLASS}):hover`]:
    { backgroundColor: 'transparent !important' },
  [`${ROW}.${SHOWN_ROW_CLASS}, ${ROW}.${SHOWN_ROW_CLASS}:hover`]: {
    backgroundColor: `${tint} !important`,
  },
})

const NO_API_FILTERS: ApiFilter[] = []

const AUTO_ROW_HEIGHT = () => 'auto' as const

function DataGridPremiumDemo({
  columns,
  rows = defaultRows,
  hideFooter = false,
  hideHeader = false,
  headerPosition = 'top',
  pageSize: initialPageSize = 50,
  checkboxSelection = false,
  width = '100%',
  onRowDoubleClick,
  onRowClick,
  apiRefExternal,
  rowSelectionModel: _rowSelectionModel,
  onRowSelectionModelChange: _onRowSelectionModelChange,
  onSelectedCountChange: _onSelectedCountChange,
  onAllRowsInfo: _onAllRowsInfo,
  selectedRowId,
  shownRowId,
  onSelectedRowIdChange: _onSelectedRowIdChange,
  maxHeight = '100%',
  primaryTable: _primaryTable = true,
  refreshExportRows: _refreshExportRows = -1,
  fakeRowsCount: _fakeRowsCount = null,
  serverSideRowCount,
  serverSidePage,
  onPaginationChange,
  paginationMode = 'client',
  controlledPage,
  onPageChange,
  onSortChange,
  sortingMode = 'client',
  externalSortModel,
  loading = false,
  rowHeight = 34,
  autoRowHeight = false,
  columnHeaderHeight = 36,
  noRowsLabel = 'אין נתונים',
  columnSettingsKey,
  twoLineHeaders = false,
  filterMode = 'client',
  gridSx,
  ownsTableStore = true,
}: {
  columns: (GridColDef & { format?: CellFormat; fitHeader?: boolean })[]
  rows?: any[]
  hideFooter?: boolean
  hideHeader?: boolean
  headerPosition?: 'top' | 'bottom'
  pageSize?: number
  checkboxSelection?: boolean
  width?: string | number
  onRowDoubleClick?: (row: any) => void
  onRowClick?: (row: any) => void
  apiRefExternal?: React.MutableRefObject<GridApiPremium | null>
  rowSelectionModel?: RowSelectionModelV7
  onRowSelectionModelChange?: (model: RowSelectionModelV7) => void
  onSelectedCountChange?: (count: number) => void
  onAllRowsInfo?: (ids: (string | number)[]) => void
  selectedRowId?: string | number | null
  /**
   * The row a neighbouring view follows, marked apart from the checked rows: the checkbox
   * selection stops tinting rows while this prop is passed, so only the shown row is tinted.
   */
  shownRowId?: string | number | null
  onSelectedRowIdChange?: (id: string | number | null) => void
  maxHeight?: string | number
  primaryTable?: boolean
  refreshExportRows?: number
  fakeRowsCount?: number | null
  serverSideRowCount?: number
  serverSidePage?: number
  onPaginationChange?: (page: number, pageSize: number) => void
  paginationMode?: 'client' | 'server'
  controlledPage?: number
  onPageChange?: (page: number) => void
  onSortChange?: (field: string, order: 'ASC' | 'DESC') => void
  sortingMode?: 'client' | 'server'
  externalSortModel?: GridSortModel
  loading?: boolean
  rowHeight?: number
  /** Rows grow to fit cells that wrap; no row is shorter than rowHeight. */
  autoRowHeight?: boolean
  columnHeaderHeight?: number
  noRowsLabel?: string
  /** Where the column order and visibility are saved; by default, per page path. */
  columnSettingsKey?: string
  /**
   * The title takes the first header line and the filter, sort and menu buttons the second, so a
   * narrow column keeps its whole title while the buttons show. Needs a columnHeaderHeight of
   * about 56.
   */
  twoLineHeaders?: boolean
  /**
   * Who applies the column filters. By default the grid filters the rows it holds. A page whose
   * Gateway request already carries the filters passes 'server' and the grid shows its rows as
   * they are.
   */
  filterMode?: 'client' | 'server'
  /** Styles one page adds to the grid; they win over the shared styles. */
  gridSx?: SxProps<Theme>
  /**
   * The column filters, filter options and export read one store, so a page with two grids
   * lets one of them own it. A grid passed false applies no column filter and writes nothing
   * to the store, so a filter on the other grid's columns cannot empty it.
   */
  ownsTableStore?: boolean
}) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [localSortModel, setLocalSortModel] = useState<GridSortModel>(externalSortModel ?? [])

  useEffect(() => {
    setLocalSortModel(externalSortModel ?? [])
  }, [externalSortModel])

  const colors = {
    background: isDarkMode ? '#091327' : '#FFFFFF',
    columnHeaderBg: isDarkMode ? '#1D325A' : '#EDF3F9',
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    headerText: isDarkMode ? '#FFFFFF' : '#003F61',
    border: 'none',
    rowBg: isDarkMode ? '#091327' : '#FFFFFF',
    rowHover: isDarkMode ? '#0B47A8' : '#DDF4FF',
    scrollTrack: isDarkMode ? '#0d1a33' : '#F8FAFC',
    scrollThumb: isDarkMode ? '#1D325A' : '#E2E8F0',
    divider: isDarkMode ? '#1D325A' : '#DDE6EF',
  }

  const internalApiRef = useGridApiRef()
  const apiRef = apiRefExternal ?? internalApiRef
  const {
    setColumnsStore,
    setRowsStore,
    setExportColumnsStore,
    setExportRowsStore,
    apiFilters: storeApiFilters,
  } = useTableStore()
  const apiFilters = ownsTableStore ? storeApiFilters : NO_API_FILTERS

  const location = useLocation()
  const storageKey = useMemo(
    () => columnSettingsKey ?? getColumnStorageKey(location.pathname),
    [columnSettingsKey, location.pathname]
  )
  const savedSettings = useMemo(() => getColumnSettings(storageKey), [storageKey])
  const initialColumnVisibilityModel = useMemo(() => {
    if (!savedSettings?.visibility) return {}
    return Object.fromEntries(
      Object.entries(savedSettings.visibility).filter(([, visible]) => !visible).map(([field]) => [field, false])
    )
  }, [savedSettings])

  const storedPageSize = Number(localStorage.getItem('table-rows-preference'))
  const defaultPageSize = storedPageSize === 25 ? 50 : (storedPageSize || initialPageSize)
  const [localPageSize, setLocalPageSize] = useState(defaultPageSize)
  const [localPage, setLocalPage] = useState(controlledPage ?? 0)

  const page = paginationMode === 'server' ? (serverSidePage ?? 0) : localPage
  // In server mode the parent owns page size because it is also used in the
  // backend request. Keeping a separate stored value here can make the footer
  // show a different size than the page returned by the API.
  const pageSize = paginationMode === 'server' ? initialPageSize : localPageSize
  const gridPage = paginationMode === 'server' ? 0 : page

  const [filteredRows, setFilteredRows] = useState(rows)

  const rowsMatchingFilters = useMemo(() => {
    if (filterMode === 'server' || apiFilters.length === 0) return rows
    const filtersByField = apiFilters.reduce<Record<string, ApiFilter[]>>((result, filter) => {
      ; (result[filter.name] ??= []).push(filter)
      return result
    }, {})

    const kindByField = new Map<string, FilterKind>()
    const kindOf = (field: string): FilterKind => {
      let kind = kindByField.get(field)
      if (!kind) {
        const column = columns.find(col => col.field === field)
        const resolved = column ? resolveColumnFilter(column, rows).kind : 'none'
        kind = resolved === 'none' ? 'text' : resolved
        kindByField.set(field, kind)
      }
      return kind
    }

    return rows.filter(row =>
      Object.entries(filtersByField).every(([field, fieldFilters]) =>
        cellPassesFilters(kindOf(field), fieldFilters, {
          raw: getTableFilterValue(row, field),
          name: valueNameOf(row, field),
        })
      )
    )
  }, [apiFilters, rows, columns, filterMode])

  const handlePaginationModelChange = (model: { page?: number; pageSize?: number }) => {
    const newPage = model.page ?? 0
    const newPageSize = model.pageSize ?? localPageSize
    setLocalPageSize(newPageSize)
    if (paginationMode === 'server' && onPaginationChange) {
      onPaginationChange(newPage, newPageSize)
    } else {
      setLocalPage(newPage)
      onPageChange?.(newPage)
    }
  }

  const emptyStateOverlay = useMemo(() => {
    const EmptyStateOverlay = () => (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.text,
          fontFamily: 'Rubik, sans-serif',
          fontSize: '15px',
        }}
      >
        {noRowsLabel}
      </Box>
    )
    return EmptyStateOverlay
  }, [colors.text, noRowsLabel])

  const slots = useMemo(() => ({
    columnMenu: (params: GridColumnMenuProps) => <CustomColumnMenu {...params} />,
    columnHeaderFilterIconButton: (params: ColumnHeaderFilterIconButtonProps) => <CustomColumnFilterMenu params={params} />,
    noRowsOverlay: emptyStateOverlay,
    noResultsOverlay: emptyStateOverlay,
  }), [emptyStateOverlay])

  // Columns memoization (apply saved order, then visual defaults)
  const columnsWithCustomRender = useMemo(() => {
    if (!columns) return columns
    let ordered = columns
    if (savedSettings?.order && savedSettings.order.length > 0) {
      const orderMap = new Map(savedSettings.order.map((field, i) => [field, i]))
      ordered = [...columns].sort((a, b) => {
        const aIdx = orderMap.get(a.field)
        const bIdx = orderMap.get(b.field)
        if (aIdx === undefined && bIdx === undefined) return 0
        if (aIdx === undefined) return 1
        if (bIdx === undefined) return -1
        return aIdx - bIdx
      })
    }
    return ordered.map(col => {
      const headerNameStr = String(col.headerName || '')
      const textWidth = Math.ceil(headerNameStr.length * 9.5) + 75
      const effectiveMinWidth =
        col.fitHeader === false
          ? (col.minWidth ?? 130)
          : Math.max(col.minWidth || 130, textWidth)
      const { format } = col

      return {
        ...col,
        ...(format && {
          valueFormatter: (value: unknown) => format(value).text,
        }),
        minWidth: effectiveMinWidth,
        width: col.width ? Math.max(col.width, effectiveMinWidth) : undefined,
        headerAlign: 'center',
        align: 'center',
        // Keep missing backend values visible in every table. Custom cell
        // renderers still control formatting, but an empty result gets the
        // same placeholder as a plain empty cell.
        renderCell: (params: any) => {
          // Not formattedValue for every column: MUI's own number and date
          // formatters would change how other grids show their values.
          const shown = format ? params.formattedValue : params.value
          const rendered = col.renderCell ? col.renderCell(params) : shown
          return isEmptyTableCellValue(rendered) ? '-' : isolateNumber(rendered)
        },
      }
    })
  }, [columns, savedSettings])

  const [_localSelectedId, setLocalSelectedId] = useState<string | number | null>(selectedRowId ?? null)

  useEffect(() => {
    if (selectedRowId !== undefined) {
      setLocalSelectedId(selectedRowId ?? null)
    }
  }, [selectedRowId])

  const computedSelectionModel = useMemo(() => {
    if (_rowSelectionModel) return _rowSelectionModel
    if (_localSelectedId != null) {
      return { type: 'include' as const, ids: new Set([_localSelectedId]) }
    }
    return undefined
  }, [_rowSelectionModel, _localSelectedId])

  useEffect(() => {
    if (paginationMode === 'server' || hideFooter) {
      setFilteredRows(rowsMatchingFilters)
    } else {
      // Basic client filtering and pagination
      setFilteredRows(rowsMatchingFilters.slice(page * pageSize, (page + 1) * pageSize))
    }
  }, [rowsMatchingFilters, page, pageSize, paginationMode, hideFooter])

  useEffect(() => {
    if (!ownsTableStore) return
    setColumnsStore(columns)
    setRowsStore(rows)
  }, [ownsTableStore, columns, rows, setColumnsStore, setRowsStore])

  useEffect(() => {
    if (!ownsTableStore) return
    setExportColumnsStore(toExportColumns(columnsWithCustomRender))
    setExportRowsStore(filteredRows)
  }, [
    ownsTableStore,
    columnsWithCustomRender,
    filteredRows,
    setExportColumnsStore,
    setExportRowsStore,
  ])

  const theme = useMemo(() => createTheme({
    direction: 'rtl',
    palette: { mode },
    typography: {
      fontFamily: '"Rubik", "Helvetica", "Arial", sans-serif',
    },
  }, heIL), [mode])




  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: width,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: maxHeight || '100%',
          direction: 'rtl',
          overflow: 'hidden',
          borderRadius: 0,
          bgcolor: colors.background
        }}
      >
        {/* TOP HEADER/PAGINATION (If configured as top) */}
        {!hideHeader && headerPosition === 'top' && (
          <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: colors.background, width: '100%' }}>
            <TableHeader
              page={page}
              pageSize={pageSize}
              rowCount={serverSideRowCount ?? rows.length}
              onPageChange={(p) => handlePaginationModelChange({ page: p })}
              setPageSize={(s) => handlePaginationModelChange({ pageSize: s })}
              isDarkMode={isDarkMode}
              position="top"
            />
          </Box>
        )}

        {/* MAIN DATA GRID AREA (Takes all remaining space and scrolls internally) */}
        <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          <StyledDataGrid
            apiRef={apiRef}
            rows={filteredRows}
            columns={columnsWithCustomRender as any}
            localeText={{
              ...GRID_LOCALE_TEXT,
              noRowsLabel,
              noResultsOverlayLabel: noRowsLabel,
            }}
            initialState={initialColumnVisibilityModel ? {
              columns: { columnVisibilityModel: initialColumnVisibilityModel },
            } : undefined}
            paginationModel={{ page: gridPage, pageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            onColumnOrderChange={() => {
              if (!apiRef.current) return
              const allCols = apiRef.current.getAllColumns()
              const visibleCols = new Set(apiRef.current.getVisibleColumns().map(c => c.field))
              saveColumnSettings(storageKey, {
                order: allCols.filter(c => !c.field.startsWith('__')).map(c => c.field),
                visibility: Object.fromEntries(
                  allCols.filter(c => !c.field.startsWith('__')).map(c => [c.field, visibleCols.has(c.field)])
                ),
              })
            }}
            onColumnVisibilityModelChange={(model) => {
              if (!apiRef.current) return
              const allCols = apiRef.current.getAllColumns()
              saveColumnSettings(storageKey, {
                order: allCols.filter(c => !c.field.startsWith('__')).map(c => c.field),
                visibility: Object.fromEntries(
                  allCols.filter(c => !c.field.startsWith('__')).map(c => [c.field, model[c.field] !== false])
                ),
              })
            }}
            slots={slots}
            pagination={paginationMode === 'client' && !hideFooter}
            sortingMode={sortingMode}
            sortModel={externalSortModel ?? (sortingMode === 'server' ? localSortModel : undefined)}
            sortingOrder={sortingMode === 'server' ? ['desc', 'asc'] : ['desc', 'asc', null]}
            onSortModelChange={(model) => {
              setLocalSortModel(model)
              if (sortingMode !== 'server') return

              const sortItem = model[0]
              if (!onSortChange) return
              // An empty model is never a user action here: sortingOrder has no null, so
              // a click only flips desc/asc. The grid emits an empty model when the
              // page asked for a sort field it does not show, which it drops. Reporting
              // that as a sort change made the page refetch, unmount the grid while
              // loading, and remount it into the same empty model, without end.
              if (!sortItem?.field || !sortItem.sort) return

              onSortChange(sortItem.field, sortItem.sort === 'asc' ? 'ASC' : 'DESC')
            }}
            checkboxSelection={checkboxSelection}
            rowSelectionModel={computedSelectionModel}
            onRowSelectionModelChange={_onRowSelectionModelChange}
            loading={loading}
            slotProps={gridLoadingSlotProps(rows.length > 0)}
            disableRowSelectionOnClick
            hideFooter={true}
            rowHeight={rowHeight}
            getRowHeight={autoRowHeight ? AUTO_ROW_HEIGHT : undefined}
            columnHeaderHeight={columnHeaderHeight}
            getRowClassName={shownRowClassName(shownRowId)}
            onRowClick={(params) => {
              setLocalSelectedId(params.id)
              _onSelectedRowIdChange?.(params.id)
              onRowClick?.(params.row)
            }}
            onRowDoubleClick={(params) => {
              onRowDoubleClick?.(params.row)
            }}
            sx={[{
              height: '100%',
              border: 'none',
              color: colors.text,
              ...gridLoadingSx(Boolean(loading), rows.length > 0),
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: `${colors.columnHeaderBg} !important`,
                color: `${colors.headerText} !important`,
                borderBottom: `1px solid ${colors.divider}`,
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: `${colors.columnHeaderBg} !important`,
                color: `${colors.headerText} !important`,
                padding: '0 8px !important',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                padding: 0,
                minWidth: 0,
                overflow: 'hidden',
                gap: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexDirection: 'row',
              },
              '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                minWidth: 0,
                flex: '1 1 auto',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-iconButtonContainer': {
                margin: '0 !important',
                width: 'auto',
                minWidth: 0,
                flex: '0 0 auto',
                overflow: 'visible',
              },
              '& .MuiDataGrid-menuIcon': {
                margin: '0 !important',
                width: 0,
                minWidth: 0,
                opacity: 0,
                flex: '0 0 auto',
                overflow: 'hidden',
                transition: 'opacity 0.15s ease, width 0.15s ease',
              },
              '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
                width: 'auto',
                opacity: 1,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '13px',
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '1.2',
                fontFamily: 'Rubik, sans-serif',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${colors.divider}`,
                color: colors.text,
                fontSize: '13px',
                fontFamily: 'Rubik, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              },
              '& .MuiCheckbox-root': {
                color: isDarkMode ? '#445876' : '#94A3B8',
                borderRadius: '6px',
                '&.Mui-checked': {
                  color: isDarkMode ? '#72A4EB' : '#1D4ED8',
                },
              },
              '& .MuiDataGrid-virtualScroller': {
                backgroundColor: colors.rowBg,
                // MUI's overlay wrapper can collapse to width 0 and follow the
                // horizontally scrolled content in RTL tables. Keep empty and
                // no-results messages centered in the visible table body.
                '& .MuiDataGrid-overlayWrapper': {
                  position: 'absolute !important',
                  inset: 0,
                  width: '100% !important',
                  height: '100% !important',
                  display: 'flex !important',
                  alignItems: 'center !important',
                  justifyContent: 'center !important',
                  pointerEvents: 'none',
                },
                '& .MuiDataGrid-overlayWrapperInner': {
                  width: '100% !important',
                  height: '100% !important',
                  display: 'flex !important',
                  alignItems: 'center !important',
                  justifyContent: 'center !important',
                },
                '&::-webkit-scrollbar': { width: 6, height: 6 }, 
                '&::-webkit-scrollbar-thumb': { backgroundColor: colors.scrollThumb, borderRadius: 3 },
                '&::-webkit-scrollbar-track': { backgroundColor: colors.scrollTrack }
              },
              '& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
                minHeight: rowHeight,
                py: '4px',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: `${colors.rowHover} !important`
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: `${colors.rowHover} !important`
              },
              ...(shownRowId !== undefined && shownRowSx(colors.rowHover)),
              // In RTL, the column boundary is on the left side of the cell
              '& .MuiDataGrid-columnSeparator': {
                left: '0 !important',
                right: 'unset !important',
              },
              ...(twoLineHeaders && {
                '& .MuiDataGrid-columnHeaderDraggableContainer': {
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignContent: 'center',
                  justifyContent: 'center',
                  columnGap: '4px',
                },
                // The buttons move up a level so they can wrap below the title.
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  display: 'contents',
                },
                '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                  flex: '1 0 100%',
                  justifyContent: 'center',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  textAlign: 'center',
                },
              }),
            }, ...(Array.isArray(gridSx) ? gridSx : [gridSx])]}
          />
        </Box>

        {/* BOTTOM FOOTER/PAGINATION (If configured as bottom or no custom position) */}
        {!hideHeader && (headerPosition === 'bottom' || !headerPosition) && (
          <Box sx={{ position: 'sticky', bottom: 0, zIndex: 10, bgcolor: colors.background, width: '100%' }}>
            <TableHeader
              page={page}
              pageSize={pageSize}
              rowCount={serverSideRowCount ?? rows.length}
              onPageChange={(p) => handlePaginationModelChange({ page: p })}
              setPageSize={(s) => handlePaginationModelChange({ pageSize: s })}
              isDarkMode={isDarkMode}
              position="bottom"
            />
          </Box>
        )}
      </Box>
    </ThemeProvider>
  )
}
export default React.memo(DataGridPremiumDemo)
